import React, { useEffect, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
} from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Three drop targets for task eligibility. Column key maps to persisted
// eligibility: 'all' => all-credit, 'slush' => mixed, 'exclude' => not counted.
export const BOARD_COLUMNS = [
  { key: 'all', title: 'Full credit', hint: 'Every entry counts', accent: '#2e7d32', bg: 'rgba(46,125,50,0.06)' },
  { key: 'slush', title: 'Slush (mixed)', hint: 'AI + review decides per entry', accent: '#ef6c00', bg: 'rgba(239,108,0,0.06)' },
  { key: 'exclude', title: 'Excluded', hint: 'Not counted', accent: '#757575', bg: 'rgba(117,117,117,0.06)' },
];

const fmtHours = (h) => {
  const n = Number(h);
  if (!Number.isFinite(n)) return '0';
  return String(Math.round((n + Number.EPSILON) * 100) / 100);
};

function TaskCard({ task, dragging }) {
  if (!task) return null;
  return (
    <div
      style={{
        border: '1px solid #dcdcdc',
        borderRadius: 8,
        background: '#fff',
        padding: '8px 10px',
        boxShadow: dragging ? '0 6px 16px rgba(0,0,0,0.18)' : '0 1px 2px rgba(0,0,0,0.06)',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.25 }}>{task.label}</div>
      {task.parentTaskName ? (
        <div className="text-muted" style={{ fontSize: 11, lineHeight: 1.25 }}>
          Parent: {task.parentTaskName}
        </div>
      ) : null}
      <div className="text-muted" style={{ fontSize: 11 }}>
        {task.entries} entries · {fmtHours(task.hours)} h · #{task.taskId}
      </div>
    </div>
  );
}

function SortableCard({ task }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.taskId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    marginBottom: 8,
    cursor: 'grab',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TaskCard task={task} />
    </div>
  );
}

function DroppableColumn({ col, ids, tasksById }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.key });
  const totalHours = ids.reduce((s, id) => s + (Number(tasksById[id]?.hours) || 0), 0);
  return (
    <div style={{ flex: '1 1 0', minWidth: 240 }}>
      <div className="d-flex justify-content-between align-items-baseline mb-1">
        <div>
          <span style={{ fontWeight: 700, color: col.accent }}>{col.title}</span>
          <span className="text-muted small ms-2">{col.hint}</span>
        </div>
        <span className="text-muted small">{ids.length} · {fmtHours(totalHours)} h</span>
      </div>
      <div
        ref={setNodeRef}
        style={{
          minHeight: 160,
          borderRadius: 10,
          padding: 8,
          background: isOver ? 'rgba(0,0,0,0.05)' : col.bg,
          border: `2px dashed ${col.accent}55`,
          transition: 'background 120ms',
        }}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {ids.map((id) => (
            <SortableCard key={id} task={tasksById[id]} />
          ))}
        </SortableContext>
        {ids.length === 0 ? (
          <div className="text-muted small text-center" style={{ padding: '32px 0' }}>Drop tasks here</div>
        ) : null}
      </div>
    </div>
  );
}

// Controlled-ish board: seeds internal column state from `initialColumns`
// (remount via `key` to reseed), and reports every change through onChange.
export default function TaskDndBoard({ initialColumns, tasksById, onChange }) {
  const [cols, setCols] = useState(initialColumns);
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    if (onChange) onChange(cols);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols]);

  const findColumn = (id) => {
    if (Object.prototype.hasOwnProperty.call(cols, id)) return id; // id is a column key
    return BOARD_COLUMNS.find((c) => cols[c.key].includes(id))?.key || null;
  };

  const handleDragStart = ({ active }) => setActiveId(active.id);

  const handleDragOver = ({ active, over }) => {
    if (!over) return;
    const from = findColumn(active.id);
    const to = findColumn(over.id);
    if (!from || !to || from === to) return;
    setCols((prev) => {
      const fromItems = [...prev[from]];
      const toItems = [...prev[to]];
      const idx = fromItems.indexOf(active.id);
      if (idx < 0) return prev;
      fromItems.splice(idx, 1);
      let insertAt = toItems.indexOf(over.id);
      if (insertAt < 0) insertAt = toItems.length;
      toItems.splice(insertAt, 0, active.id);
      return { ...prev, [from]: fromItems, [to]: toItems };
    });
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over) return;
    const from = findColumn(active.id);
    const to = findColumn(over.id);
    if (from && to && from === to) {
      setCols((prev) => {
        const items = prev[from];
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return prev;
        return { ...prev, [from]: arrayMove(items, oldIndex, newIndex) };
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <div className="d-flex flex-wrap gap-3 align-items-start">
        {BOARD_COLUMNS.map((col) => (
          <DroppableColumn key={col.key} col={col} ids={cols[col.key] || []} tasksById={tasksById} />
        ))}
      </div>
      <DragOverlay>{activeId ? <TaskCard task={tasksById[activeId]} dragging /> : null}</DragOverlay>
    </DndContext>
  );
}
